import { OVERDUE_EMAIL_STEP, isOverdueRefundStep, isOverdueNoteStep, overdueRestoredStates } from './overdueRefund.js'
import { fillReturnStageDates, chosenCandidateNote, addressOneLine, taskPathChoiceStep, returnStagePathKey, refundOverdueWindowOf, RETURN_STAGE_DELIVERED_OVERDUE } from '@cxhub/shared'
// Pure, React-free logic for SuggestionPanel.jsx's actionInputs state —
// extracted so it can be unit-tested with node --test (no React harness
// available).

// MM/DD/YYYY, same America/New_York timezone convention orderAgeInfo() (App.jsx)
// uses for its day-boundary calculation, so the two never disagree near
// midnight. Shared here (rather than living in App.jsx, which imports
// ManualActionsSidebar.jsx — a circular import) so both can use the same
// house date-formatting convention.
export function formatOrderDateMdy(orderDateIso) {
  if (!orderDateIso) return null
  const d = new Date(orderDateIso)
  if (isNaN(d.getTime())) return null
  return d.toLocaleDateString('en-US', { timeZone: 'America/New_York', month: '2-digit', day: '2-digit', year: 'numeric' })
}

// One "other orders" dropdown option (App.jsx's order header), on one line,
// " - " separated, empty values skipped, in this order (2026-10-01): PO number
// - order date (MM/DD/YYYY) - status - email - phone - erpOrderId - eBay
// payment ID (2026-10-28, eBay orders only). The
// vehicle can be long, so it is not part of this line: the menu shows it on a
// second line of its own (o.vehicle).
// candidateOrders is stored per suggestion, so an older entry carries only
// erpOrderId/orderDate/status/orderTotal — whatever is present renders.
export function orderCandidateOptionText(o) {
  return [o?.poNumber, formatOrderDateMdy(o?.orderDate), o?.status, o?.emailAddress, o?.billingPhone, o?.erpOrderId, o?.paymentId]
    .map((v) => (typeof v === 'string' ? v.trim() : v))
    .filter(Boolean)
    .join(' - ')
}

// requiresInput actions: a plain requiresInput action is satisfied by a
// non-empty string; a process_return_refund-style action (itemSelection
// present) additionally needs a reason code AND at least one item selected —
// unless hideReasonCode (offer_partial_refund_ebay's own eBay "Make an
// Offer" flow, which has no reason code to pick at all), which only needs
// the item selection; a process_replacement-style action (itemSelection +
// shippingSelection) additionally needs a shipping method and a complete address.
// An item already on an RTN (2026-10-27, server/src/ai/returnable-quantity.js):
// a return/refund line selected beyond its returnableQuantity. The console
// refuses a new return for it (ERR-TRQ114); Support refunds it on that RTN.
// Returns the panel's lines, "<SKU> x 1 is already on <RTN> ($0.00,
// 10/22/2026).", one per such selected item; empty when none.
export const ALREADY_RETURNED_HINT = "A new return isn't allowed. Transfer to Support to refund it on that return."
export function alreadyReturnedSelection(a, value) {
  if (a?.type !== 'process_return_refund') return []
  const all = a.itemSelection?.allItems ?? []
  return (value?.items ?? []).flatMap((si) => {
    const line = all.find((x) => x?.sku && x.sku === si?.sku)
    if (!line || line.returnableQuantity == null) return []
    if ((Number(si.quantity) || 1) <= Number(line.returnableQuantity)) return []
    return [line.alreadyOnRtn ?? `${line.sku} is already on a return.`]
  })
}

export function isActionInputSatisfied(a, value) {
  if (!a.requiresInput) return true
  if (a.itemSelection) {
    // Never Process a line beyond what is returnable (2026-10-27).
    const itemsOk = (a.hideReasonCode || Boolean(value?.reasonCode)) && Array.isArray(value?.items) && value.items.length > 0 && !alreadyReturnedSelection(a, value).length
    if (!a.shippingSelection) return itemsOk
    const addr = value?.address ?? {}
    const addressOk = Boolean(value?.shippingMethod) && addr.shippingName && addr.shippingAddress1 && addr.shippingCity && addr.shippingRegion && addr.shippingPostalCode
    return itemsOk && Boolean(addressOk)
  }
  // issue_ebay_return_refund (the numbered-Proposed-Step counterpart of
  // RefundModal.jsx's own Full/Partial refund modal — see actions.js's
  // automatedReturnNoticeRefundOnlyActions()): a refund type and a non-empty
  // comment are always required; a partial refund additionally needs a
  // positive amount not exceeding the order's own total — same
  // isFormSubmittable() rule RefundModal.jsx already enforces for the
  // manual-button version of this same action type.
  if (a.type === 'issue_ebay_return_refund') {
    if (!value?.refundType || !value?.comment?.trim()) return false
    if (value.refundType === 'full') return true
    const amount = Number(value?.amount)
    return amount > 0 && amount <= (a.params?.orderTotal ?? 0)
  }
  // A requiresInput action whose value shape is a composed message (the
  // manual-handling inr_request_id_process step) — a non-empty OBJECT
  // ({ editedMessage: '' }) would otherwise satisfy the plain Boolean(value)
  // check below even with nothing actually typed. Checked by shape, not by
  // action type, since editedMessage is only ever set on this kind of value.
  if (value && typeof value === 'object' && 'editedMessage' in value) {
    return Boolean(value.editedMessage?.trim())
  }
  // send_return_label_email (the Website RMA path's own deferred followup —
  // action-executor.js's process_return_refund) — an editable email field,
  // defaulting to params.defaultEmail (the customer's own on-file address),
  // but still required to be non-empty before Process is enabled (an admin/
  // CSR may have cleared it to type a different address).
  if (a.type === 'send_return_label_email') {
    return Boolean((value?.email ?? a.params?.defaultEmail)?.trim())
  }
  // resend_return_label (Website B-recover/B-create only — eBay's own flows
  // never set requiresInput at all) — the "Send label to" field, defaulting
  // to params.shipTo?.emailAddress (the order's own on-file address). A
  // full isSingleValidEmail() check, not just non-empty — feature/
  // website-resend-label-decision's own Process-button gate, same rule the
  // Website RMA path's own email field already applies.
  if (a.type === 'resend_return_label') {
    // params.defaultSendTo (2026-10-27): the contact's own email when the
    // order has none (server/src/ai/label-send-to.js).
    return isSingleValidEmail(value?.email ?? labelSendToDefault(a))
  }
  return Boolean(value)
}

// The agent's new return label (2026-11-02, server/src/ai/agent-own-steps.js's
// withReplyLinkSlot()): its reply has no place for the link
// (params.replyHasLinkSlot false), so nothing is added to the reply. Once the
// step is processed and a label link exists, the step's result shows it with
// a Copy button and this line.
export const LABEL_LINK_NOT_IN_REPLY = "The reply has no place for the label link, so it wasn't added: copy the link above into the reply if the customer needs it."
export function labelLinkNotInReply(a, result) {
  if (a?.type !== 'resend_return_label' || a?.params?.flow !== 'B-create' || a?.params?.replyHasLinkSlot !== false) return false
  const links = [...(Array.isArray(result?.labels) ? result.labels.map((l) => l?.labelUrl) : []), result?.labelUrl]
  return links.some(Boolean)
}

// A resend_return_label step's "Send label to" default (2026-10-27): the
// order's email, else params.defaultSendTo (the contact's own, set by
// server/src/ai/actions.js's labelSendToParams()). undefined when neither.
export function labelSendToDefault(a) {
  return a?.params?.shipTo?.emailAddress || a?.params?.defaultSendTo || undefined
}

// The packages a label-create step starts with (2026-10-27, one label per
// box): one row per box from the grounding's box plan
// (labelCreateData.packages), else one row at the step's weight.
export function defaultLabelPackages(a) {
  const pk = a?.params?.labelCreateData?.packages
  if (Array.isArray(pk) && pk.length) return pk.map((p) => ({ weight: Number(p?.weight) || 0 }))
  return [{ weight: a?.params?.labelCreateData?.weight ?? 0 }]
}

// Whether a step creates return labels at Process with no packages editor of
// its own elsewhere: eBay's resend B-create and the automated return notice's
// label create (the Website B-create has it under its Send label to fields).
export function showsLabelPackagesEditor(a) {
  if (!a?.targetSystem || a?.requiresInput) return false
  if (a.type === 'resend_return_label') return a.params?.flow === 'B-create' && !a.params?.trackingNumber && !a.params?.recoverAndSend
  if (a.type === 'automated_return_notice_resend_label') return !a.params?.trackingNumber
  return false
}

// What a label step's result hands the steps after it (2026-10-27): every
// box's link (labelUrls) and whether the labels went onto the buyer's eBay
// return (attachedToEbayReturn; false takes the eBay print line out), and
// labelMessage: false when one box went onto the eBay return (no message).
// stepStates: the panel's, by index. {} when no label step has run.
export function labelStepHandoff(actions = [], stepStates = {}) {
  let out = {}
  ;(actions ?? []).forEach((a, i) => {
    if (a?.type !== 'resend_return_label' && a?.type !== 'automated_return_notice_resend_label') return
    const r = stepStates?.[i]?.status === 'success' ? stepStates[i].result : null
    if (!r) return
    const urls = (r.boxLabels ?? []).map((l) => l?.labelUrl).filter(Boolean)
    out = {
      ...out,
      ...(urls.length ? { labelUrls: urls } : {}),
      ...(typeof r.attachedToEbayReturn === 'boolean' && out.attachedToEbayReturn !== false ? { attachedToEbayReturn: r.attachedToEbayReturn, ...(r.attachFallbackReason ? { attachFallbackReason: r.attachFallbackReason } : {}) } : {}),
      // One box on the eBay return (2026-11-04): no label message.
      ...(r.labelMessage === false ? { labelMessage: false } : {}),
      ...(r.flow === 'B-create' && Array.isArray(r.labels) ? { labels: r.labels.filter((l) => l?.trackingNumber && !l.error).map((l) => ({ trackingNumber: l.trackingNumber })) } : {}),
    }
  })
  return out
}

// A single, plausible email address — not a full RFC 5322 validator, just
// enough to reject empty/incomplete input and a comma-separated list (this
// field sends to exactly one address; a second recipient belongs in the
// separate cc field). Shared by SuggestionPanel.jsx's Website RMA path
// "Send label to" field (feature/website-rma-label-email-step1) and
// anywhere else a single address needs the same plain check.
export function isSingleValidEmail(value) {
  return typeof value === 'string' && /^[^\s@,]+@[^\s@,]+\.[^\s@,]+$/.test(value.trim())
}

// FROM_EBAY proposed actions that send a real customer-facing message through
// something other than the standard buyer-seller Message API reply box
// (eBay Trading API's AddMemberMessageAAQToPartner, or the Post-Order
// inquiry send_message/issue_refund endpoints) — these never go through the
// drafted-reply TextField (hidden entirely for fromEbay), so without this
// they'd send straight from an AI-resolved string a CSR never saw. Returns
// the AI-resolved default text to review/edit, or null if `a` isn't one of
// these types (used as the render gate too).
export function getEditableMessageDefault(a) {
  if (a.type === 'send_ebay_member_message') return a.params?.body ?? ''
  // automated_return_notice_send_message: automated_return_notice_default's
  // own eBay message step (server/src/ai/actions.js's
  // automatedReturnNoticeDefaultActions()) — this messageKind has no
  // draftReply/email box at all (pipeline.js sets needsDraft: false for it),
  // so the AI-drafted text lives here instead, same editable-message
  // treatment as every other case in this function.
  if (a.type === 'automated_return_notice_send_message') return a.params?.body ?? ''
  // resend_return_label_send_message: resend_return_label's eBay message
  // for every flow (server/src/ai/actions.js's resendReturnLabelActions():
  // Flow A's return-opening instructions, Flow B-recover/B-create's label
  // message), always its own step, never bundled into the note step
  // (docs/decisions.md, 2026-10-01).
  if (a.type === 'resend_return_label_send_message') return a.params?.body ?? ''
  // sales_hold_send_ebay_message: Sales Hold's own "couldn't correct the
  // address" eBay notification (actions.js's couldNotCorrectEbayNotificationAction())
  // — a real customer-facing eBay message, same as send_ebay_member_message
  // above, just proposed off a m365_email (never FROM_EBAY) conversation.
  if (a.type === 'sales_hold_send_ebay_message') return a.params?.messageText ?? ''
  // inr_send_message: the customer-facing half of the split delivered/
  // cancelled/in_processing/replacement dispositions (server/src/ai/actions.js's
  // buildInrDispositionStep()) — its own separate action now, always LAST in
  // the pair.
  if (a.type === 'inr_send_message') return a.params?.msgContent ?? ''
  // task_case_message (2026-10-15): a task's same message on the order's open
  // INR inquiry, after its buyer message.
  if (a.type === 'task_case_message') return a.params?.msgContent ?? ''
  // inr_issue_refund: the customer-facing half of the split refund/
  // undeliverable_return_to_sender dispositions — comments.content IS the
  // buyer-facing text (this call has no separate message send of its own).
  if (a.type === 'inr_issue_refund') return a.params?.inquiryRefundBody?.comments?.content ?? ''
  // inr_provide_shipment_info: the eBay-facing half of the split
  // shipped_not_delivered disposition — sellerComment is the proof-of-
  // shipment call's own seller-comments text, editable the same way every
  // other split disposition's buyer/eBay-facing text is.
  if (a.type === 'inr_provide_shipment_info') return a.params?.sellerComment ?? ''
  if (a.type === 'inr_request_id_process') {
    const d = a.params?.disposition
    // Falsy disposition (never 'already_resolved'/'already_resolved_confirmed',
    // both truthy strings with no buyer-facing text of their own) is the
    // "couldn't be auto-resolved" manual-handling case — there's no
    // AI-resolved text to show (nothing to invent), but the CSR can still
    // compose and send a reply directly to the case; return an empty string
    // rather than null so the editable field renders.
    if (!d) return ''
  }
  return null
}

// Standalone "add a note to TRQ" action types across every flow:
// address_confirmation_add_note (address-confirmation flow specifically),
// add_order_note (the generic fallback actions.js's
// maybeAddOrderNoteStep()/buildAddOrderNoteAction() appends to every other
// flow's action list, unless that flow already bundles its own note into a
// bigger write), and resend_return_label_add_note (resend-return-label's
// note, every flow — it writes only the note; an eBay message is its own
// resend_return_label_send_message step). Shared by getEditableNoteDefault()
// below (the editable-note TextField's render gate).
export function isNoteOnlyActionType(type) {
  return type === 'add_order_note' || type === 'address_confirmation_add_note' || type === 'resend_return_label_add_note'
    || type === 'refund_freight_trq' || type === 'refund_tax_trq' || type === 'sales_hold_address_validation_note'
    // issue_ebay_return_refund_add_note (actions.js's automatedReturnNoticeRefundOnlyActions()):
    // same "note only, gated on a sibling" shape as resend_return_label_add_note above.
    || type === 'issue_ebay_return_refund_add_note'
    // automated_return_notice_add_note (actions.js's automatedReturnNoticeDefaultActions()):
    // same shape again — writes no buyer-facing reply of its own.
    || type === 'automated_return_notice_add_note'
    // overdue_refund_add_note (2026-10-29, Return Status's overdue refund): the
    // refund's note, gated on the refund step.
    || type === 'overdue_refund_add_note'
}

// Action types with their OWN bespoke requiresInput UI — a specific
// a.type === '...' block in SuggestionPanel.jsx (send_return_label_email's
// email+cc fields, issue_ebay_return_refund's Full/Partial+amount fields),
// each needing more than the generic single-value TextField below can hold.
// SuggestionPanel.jsx's generic requiresInput render gate already excludes
// itemSelection/getEditableMessageDefault() types the same way — this list
// is the same exclusion for the remaining bespoke-but-neither-of-those
// cases, so a type doesn't render BOTH its own fields AND a second, useless
// "Required input" TextField underneath them.
export function hasBespokeRequiresInputUi(type) {
  // resend_return_label: only its Website B-recover/B-create sub-cases ever
  // carry requiresInput: true (eBay's own flows never do) — feature/
  // website-resend-label-decision reuses the same LabelEmailFields/
  // PackagesEditor bespoke UI the Website RMA path's own Step 1 already uses.
  return type === 'send_return_label_email' || type === 'issue_ebay_return_refund' || type === 'resend_return_label'
}

// Action types whose Process step never introduces anything NEW into the
// reply, so SuggestionPanel.jsx's handleProcessStep skips the post-Process
// draft regenerate for them (still refreshes the suggestion record itself,
// just not the draft text) — regenerating would only overwrite whatever the
// CSR was already typing with an unrelated (and often identical) AI draft:
// - the note-only types above (no reply of their own at all).
// - resend_return_label: this type's Process button covers Flow B-create
//   (eBay's B-recover step is informational) and Website's flows. On eBay
//   B-create the label link is pushed straight into the sibling
//   resend_return_label_send_message step's box once this succeeds
//   (SuggestionPanel.jsx's handleProcessStep()); regenerating here would
//   race that push, and skipping keeps apply()'s reseed from overwriting it.
// - address_confirmation_update_address / address_confirmation_remove_hold:
//   pure console writes with no buyer-facing reply of their own either —
//   same reasoning as the note-only types, just not literally "note" types.
// - automated_return_notice_resend_label: same "no reply of its own" shape —
//   the buyer-facing reply lives entirely in the sibling
//   automated_return_notice_send_message step, which SuggestionPanel.jsx's
//   handleProcessStep refreshes directly from THIS step's own result
//   (resendMessageBody) once it succeeds. Regenerating here would race that
//   direct refresh: a regenerated suggestion's own message-step default
//   depends on eBay's read-after-write consistency for the label just
//   written (unreliable within the same request), so it must never
//   overwrite the deterministic value already pushed into place. Skipping
//   here also guarantees `apply()`'s own reseed preserves that pushed value
//   (skipDraft forces the "keep whatever's already in the box" branch).
// - issue_ebay_return_refund: same "no reply of its own" shape as
//   automated_return_notice_resend_label just above — the buyer-facing reply
//   lives entirely in the sibling automated_return_notice_send_message step
//   (actions.js's automatedReturnNoticeRefundOnlyActions()), and this step's
//   own result.replyText (a plain REFUND_NO_RETURN_TEMPLATE resolution, for
//   RefundModal.jsx's separate manual-button usage of this same action type)
//   is not the text that step actually sends here. Regenerating would race
//   the sibling-note push (see issueEbayReturnRefundNoteSiblingIndex()) the
//   same way it would for automated_return_notice_resend_label.
// - inr_request_id_process, manual/no-disposition variant only (a falsy
//   params.disposition — same check getEditableMessageDefault() above uses):
//   this send goes straight to the eBay case as a Post-Order comment, never
//   through conversations.messages, so the pipeline still sees the same
//   unanswered turn afterward and can reclassify/re-propose an identical new
//   pending suggestion right next to the one that just succeeded. The OTHER
//   inr_request_id_process dispositions (and the separate inr_send_message/
//   inr_issue_refund action types) close the conversation directly
//   server-side and don't need this — scoped narrowly to the case this
//   actually fixes rather than blanket-skipping the whole action type.
// - sales_hold_call_customer_email: an internal ops email (to
//   1asalesholds@1aauto.com asking a human to call the customer) — it
//   introduces nothing new into the conversation's own customer-facing
//   drafted reply, same shape as sales_hold_address_validation_note (a
//   note-only type above). Regenerating after Process would only re-run
//   the AI pipeline and risk overwriting a draft the CSR may already be
//   reviewing/editing, for no reason.
// - process_replacement and get_erp_replacement_id (2026-10-06): the reply is
//   the replacement template, resolved by the executor once the ERP order
//   number is known (result.replyText). When it isn't known yet, the
//   regenerate would replace this suggestion with a new one and lose both
//   the step that just created the replacement and the appended
//   get_erp_replacement_id step that fetches the number.
export function shouldSkipDraftRefresh(a) {
  const type = typeof a === 'string' ? a : a?.type
  if (isNoteOnlyActionType(type) || type === 'resend_return_label'
    || type === 'address_confirmation_update_address' || type === 'address_confirmation_remove_hold'
    || type === 'automated_return_notice_resend_label' || type === 'issue_ebay_return_refund'
    || type === 'sales_hold_call_customer_email'
    || type === 'process_replacement' || type === 'get_erp_replacement_id' || type === 'task_create_refund') {
    return true
  }
  if (type === 'inr_request_id_process' && typeof a === 'object' && !a?.params?.disposition) return true
  // Return Status's overdue refund (2026-10-29): a regenerate after the refund
  // would build a new suggestion (the order now has the RTN) and drop the
  // email and the note still to process.
  if (type === OVERDUE_EMAIL_STEP || (typeof a === 'object' && (isOverdueRefundStep(a) || isOverdueNoteStep(a)))) return true
  return false
}

// Any proposed action whose params carry a `notes` field gets an editable
// box for it, full stop — a STRUCTURAL check, not a type-name allowlist.
// Previously this was three separate hardcoded type lists (isNoteOnlyActionType()
// above, plus two inline checks for cancel_order_trq/process_replacement and
// inr_create_replacement_trq/inr_disposition_create_return_trq) that all did
// the exact same `a.params?.notes ?? ''` read — the lists existed only to
// decide "does this type have a notes param worth showing," which the action
// object itself already answers directly. A real bug shipped from this: a
// new note-carrying type (sales_hold_need_physical_address_note) was never
// added to any of the three lists, so its box silently never rendered — and
// the same gap will recur for every future note-carrying type unless the
// check stops being name-based. process_return_refund has no single default
// note at proposal time (it depends on the live disposition check) — handled
// separately by noteFromPlannedSteps() below, not by this function; its
// params carry no top-level `notes` field, so it's correctly unaffected here.
export function getEditableNoteDefault(a) {
  if (a.params?.notes !== undefined) return a.params.notes ?? ''
  // issue_ebay_return_refund_add_note: like process_return_refund above, this
  // has no single default note at proposal time — unlike that type, though,
  // it's not resolved via a "Check" step at all; its content depends entirely
  // on the sibling issue_ebay_return_refund step's own refund outcome (full/
  // partial, amount, comment), so it starts empty and SuggestionPanel.jsx's
  // handleProcessStep() seeds it once that sibling succeeds (see
  // issueEbayReturnRefundNoteSiblingIndex() below). Returns '' (not null) so
  // the box still renders meanwhile, same "render it now, fill it in later"
  // treatment inr_request_id_process's own manual-handling case gets above.
  if (a.type === 'issue_ebay_return_refund_add_note') return ''
  return null
}

// Address-carrying "update shipping address" proposed steps whose Process
// button rendered bare, no editable address fields at all — same failure
// mode getEditableNoteDefault() above used to have, but the address shape
// isn't uniform across these six action types, so this stays a type dispatch
// table rather than one single structural check. Three shape families
// (confirmed by reading every builder in server/src/ai/actions.js and its
// grounding modules):
//  - address_confirmation_update_address: params ARE the address, flat
//    (shippingName, shippingAddress1, shippingAddress2, shippingCity,
//    shippingRegion, shippingPostalCode) — same field names
//    isActionInputSatisfied()'s shippingSelection.address branch above uses.
//  - sales_hold_address_validation_update / sales_hold_phone_call_update_address:
//    params.proposedAddress, nested, same shippingAddress1/shippingAddress2/
//    shippingCity/shippingRegion/shippingPostalCode field names as the flat
//    family above (no shippingName — neither flow ever resolves a recipient
//    name, only the street/city/state/zip). Both are built from each
//    branch's own Smarty/TRQ-sourced fields, so all five fields are normally
//    populated — the render code still degrades gracefully to a blank field
//    for any one that isn't, rather than omitting it.
//  - sales_hold_address_too_long / sales_hold_need_physical_address_apply:
//    params.proposedAddress, nested, but each carries its OWN 2-field-only
//    shape (newAddress1/newAddress2, physicalAddress1/physicalAddress2 —
//    sales-hold.js's checkAddressTooLongFix()/checkPhysicalAddressAlongsidePoBox()).
//    No city/state/zip is ever grounded for either of these two flows (their
//    own grounding.currentAddress is the same 2-field shippingAddress1/
//    shippingAddress2 shape, nothing to expand from), so this deliberately
//    stays a narrower 2-field form rather than normalizing to the 5-field
//    shape above, which would invent fields with no real data behind them.
export function getEditableAddressDefault(a) {
  if (a.type === 'address_confirmation_update_address') {
    const p = a.params ?? {}
    return {
      shippingName: p.shippingName ?? '',
      shippingAddress1: p.shippingAddress1 ?? '',
      shippingAddress2: p.shippingAddress2 ?? '',
      shippingCity: p.shippingCity ?? '',
      shippingRegion: p.shippingRegion ?? '',
      shippingPostalCode: p.shippingPostalCode ?? '',
    }
  }
  if (a.type === 'sales_hold_address_validation_update' || a.type === 'sales_hold_phone_call_update_address') {
    const p = a.params?.proposedAddress ?? {}
    return {
      shippingAddress1: p.shippingAddress1 ?? '',
      shippingAddress2: p.shippingAddress2 ?? '',
      shippingCity: p.shippingCity ?? '',
      shippingRegion: p.shippingRegion ?? '',
      shippingPostalCode: p.shippingPostalCode ?? '',
    }
  }
  if (a.type === 'sales_hold_address_too_long') {
    const p = a.params?.proposedAddress ?? {}
    return { newAddress1: p.newAddress1 ?? '', newAddress2: p.newAddress2 ?? '' }
  }
  if (a.type === 'sales_hold_need_physical_address_apply') {
    const p = a.params?.proposedAddress ?? {}
    return { physicalAddress1: p.physicalAddress1 ?? '', physicalAddress2: p.physicalAddress2 ?? '' }
  }
  return null
}

// True when an editedAddress value has anything a CSR actually typed/kept —
// used the same way editedMessage/editedNote's own `?.trim()` check is used
// below, just generalized to an object of fields rather than a single string.
function hasAddressContent(addr) {
  return Boolean(addr) && typeof addr === 'object' && Object.values(addr).some((v) => typeof v === 'string' && v.trim())
}

// process_return_refund resolves to one of several different note strings
// depending on the live disposition check ("Check if scrap") — no single
// default is known at proposal time, unlike cancel_order_trq/
// process_replacement above. server/src/ai/return-refund.js's
// buildPlannedSteps() (the check result) already returns each note-bearing
// path's real apiCall.body.notes text; this pulls the first one found so the
// UI can pre-fill the same editable box once a check succeeds. Returns null
// when no step in the result carries a notes field (e.g. the Gen Instructions
// path, which makes no TRQ write at all — an empty plannedSteps array or one
// with no notes field both resolve to null here).
export function noteFromPlannedSteps(plannedSteps) {
  for (const step of plannedSteps ?? []) {
    if (typeof step?.apiCall?.body?.notes === 'string') return step.apiCall.body.notes
  }
  return null
}

// Replaces exactly the PREVIOUS auto-captured line/block (if the note still
// ends with it) with the new one — so toggling options/re-editing the
// deduction never leaves a stale or duplicated line behind, without
// disturbing anything else the CSR typed above it. Also strips the blank-
// line separator that was prepended ahead of the previous line/block (the
// same "\n\n" this function itself adds below) — without this, re-running
// it a second time (once the previous line/block is itself the product of
// an earlier call, e.g. seeded at Check time and then replaced again on a
// toggle) would leave that separator dangling and double it up. Passing
// newLine: null just removes the previous line/block entirely (e.g. going
// back to the untouched Customer-Cost default, or SuggestionPanel.jsx's own
// cross-path cleanup when a disposition override flips RMA to scrap).
export function withShippingNoteLine(baseNote, previousLine, newLine) {
  let note = baseNote ?? ''
  if (previousLine && note.endsWith(previousLine)) {
    note = note.slice(0, note.length - previousLine.length)
    if (note.endsWith('\n\n')) note = note.slice(0, -2)
  }
  if (newLine) {
    note = note ? `${note}\n\n${newLine}` : newLine
  }
  return note
}

// Sibling to withShippingNoteLine() above, for the Website scrap-refund
// path's own auto-captured "Total refund: $X.XX." suffix — a single
// trailing sentence appended directly onto the base note (no blank-line
// block), so it replaces the PREVIOUS suffix (if the note still ends with
// it) rather than stacking a new one on every re-check. Passing newSuffix:
// null just strips the previous suffix (SuggestionPanel.jsx's own cross-path
// cleanup when a disposition override flips scrap to RMA).
export function withTotalRefundSuffix(baseNote, previousSuffix, newSuffix) {
  let note = baseNote ?? ''
  if (previousSuffix && note.endsWith(previousSuffix)) note = note.slice(0, note.length - previousSuffix.length)
  return newSuffix ? `${note}${newSuffix}` : note
}

// Builds the actionInputs seed for a freshly-applied suggestion (initial
// load, Regenerate, or the automatic refresh after a step's Process call
// succeeds). Preserves an action's PREVIOUS value as-is when it already
// satisfies isActionInputSatisfied(), instead of blindly reseeding from the
// freshly re-derived action's defaults — regrounding (e.g. after a successful
// Process call, which triggers this same reseed via generateForConversation()
// re-running) has no awareness of what's already been confirmed, and for
// damagedReplacementActions()/missingPartsReplacementActions() (actions.js)
// the itemSelection default is deliberately [] every time, which would
// otherwise silently wipe out items/address/reason the CSR already
// checked/typed for a step that's already been (or is being) processed.
// resolvePlaceholder: applied to the seeded editedMessage default — the AI
// text is baked at generation time with the literal [AGENT_FIRST_NAME]
// token (see getEditableMessageDefault() above), same as the main draft box,
// which resolves it via SuggestionPanel.jsx's resolveAgentFirstNamePlaceholder()
// before ever showing it. Defaults to identity (no resolution) so this stays
// a pure, React-free function callable with no resolver in tests.
// skipDraft: mirrors apply()'s own skipDraft — when true, preserves an
// already-non-empty editedMessage/editedNote instead of reseeding it from the
// freshly re-derived action's default, same "preserve if already there"
// treatment the itemSelection branch above already gets. Without this, a
// manual-panel Process on step A (or any refresh with skipDraft: true) would
// still silently overwrite a manually-templated editedMessage/editedNote
// sitting on a completely different step B — these two fields are where the
// FROM_EBAY "Reply to eBay case" box and note boxes actually live, and for
// FROM_EBAY conversations the editedMessage box is the ONLY reply surface at
// all (see SuggestionPanel.jsx's isFromEbay gate on the main draft box).
// An item selection can never hold the same SKU twice (2026-10-08): the
// table shows one checkbox per SKU, so a duplicate was invisible and went to
// TRQ as-is (contact 6074693, ERR-TRQ100 "Duplicate SKU found in return
// items"). Dedupe by sku, first occurrence wins, and cap each quantity at
// that line's ordered quantity. A SKU not on the order is left alone for the
// server to refuse.
export function normalizeItemSelection(items, allItems) {
  const bySku = new Map((allItems ?? []).filter((x) => x?.sku).map((x) => [x.sku, x]))
  const seen = new Set()
  const out = []
  for (const it of items ?? []) {
    if (!it?.sku || seen.has(it.sku)) continue
    seen.add(it.sku)
    const ordered = Number(bySku.get(it.sku)?.quantity)
    const quantity = Number.isFinite(ordered) && ordered > 0 && Number(it.quantity) > ordered ? ordered : it.quantity
    out.push({ sku: it.sku, quantity })
  }
  return out
}

// Ticking or unticking one row of the item table. A kit and its own
// components are the same physical part, so ticking a kit drops its
// components. The result goes through normalizeItemSelection(), so it always
// matches what the checkboxes show. (Moved from SuggestionPanel.jsx, which
// re-exports it, so node --test can cover it.)
export function toggleKitAwareSelection(cur, it, allItems, isChecked) {
  if (!isChecked) return normalizeItemSelection(cur.items.filter((x) => x.sku !== it.sku), allItems)
  let items = cur.items.filter((x) => x.sku !== it.sku)
  if (it.itemType === 'kit') {
    const componentSkus = new Set((allItems ?? []).filter((x) => x.lineSequence === it.lineSequence && x.itemType === 'component').map((x) => x.sku))
    items = items.filter((x) => !componentSkus.has(x.sku))
  }
  return normalizeItemSelection([...items, { sku: it.sku, quantity: it.quantity }], allItems)
}

export function reseedActionInputs(proposedActions, prevInputs, resolvePlaceholder = (text) => text, { skipDraft = false } = {}) {
  const seeded = {}
  ;(proposedActions ?? []).forEach((a, i) => {
    const prevValue = prevInputs?.[i]
    if (a.itemSelection && isActionInputSatisfied(a, prevValue)) {
      seeded[i] = Array.isArray(prevValue?.items)
        ? { ...prevValue, items: normalizeItemSelection(prevValue.items, a.itemSelection.allItems) }
        : prevValue
    } else if (a.itemSelection) {
      seeded[i] = {
        reasonCode: a.inputDefault != null ? String(a.inputDefault) : '',
        items: normalizeItemSelection(a.itemSelection.defaultSelected, a.itemSelection.allItems),
      }
      if (a.shippingSelection) {
        seeded[i].shippingMethod = a.shippingSelection.defaultMethod ?? ''
        seeded[i].address = { ...a.shippingSelection.address }
      }
    } else if (a.requiresInput && a.inputDefault != null) {
      seeded[i] = String(a.inputDefault)
    }
    const msgDefault = getEditableMessageDefault(a)
    if (msgDefault !== null) {
      const prevMsg = prevValue?.editedMessage
      seeded[i] = { ...(seeded[i] ?? {}), editedMessage: skipDraft && prevMsg?.trim() ? prevMsg : resolvePlaceholder(msgDefault) }
    }
    // The agent's own eBay refund step (2026-10-30, agent-own-steps.js):
    // its Full / Partial and amount start as the agent staged them
    // (params.presetRefund), unless the person already chose.
    const preset = a.params?.presetRefund
    if (preset && (preset.refundType === 'full' || preset.refundType === 'partial') && !(prevValue && typeof prevValue === 'object' && prevValue.refundType)) {
      seeded[i] = {
        ...(seeded[i] ?? {}),
        refundType: preset.refundType,
        ...(typeof preset.comment === 'string' && preset.comment ? { comment: preset.comment } : {}),
        amount: preset.refundType === 'partial' && preset.amount != null ? String(preset.amount) : '',
      }
    }
    const noteDefault = getEditableNoteDefault(a)
    if (noteDefault !== null) {
      const prevNote = prevValue?.editedNote
      // An eBay refund step's empty box starts with the modal's default
      // note for the inputs so far (2026-10-29, withEbayRefundNote()).
      const own = takesEbayRefundDefaultNote(a) ? ebayRefundStepNoteDefault(a, { ...(prevValue && typeof prevValue === 'object' ? prevValue : {}), ...(seeded[i] ?? {}) }) : noteDefault
      seeded[i] = { ...(seeded[i] ?? {}), editedNote: skipDraft && prevNote?.trim() ? prevNote : own }
    }
    const addressDefault = getEditableAddressDefault(a)
    if (addressDefault !== null) {
      const prevAddress = prevValue?.editedAddress
      seeded[i] = { ...(seeded[i] ?? {}), editedAddress: skipDraft && hasAddressContent(prevAddress) ? prevAddress : addressDefault }
    }
  })
  return seeded
}

// Flow B-create's eBay message step (resend_return_label_send_message,
// params.flow === 'B-create') depends on a SIBLING action's result: the label
// doesn't exist until the create-label step (type: 'resend_return_label',
// params.flow === 'B-create') succeeds, so the message can't be sent before
// that — unlike Flow B-recover's message, whose label was already known at
// generation time (no sibling dependency at all). Returns the sibling's index
// so the UI can both gate this step's Process button (disabled until that
// index's stepStates status is 'success') and pull its result (labelUrl)
// forward — see SuggestionPanel.jsx's handleProcessStep(). Returns null for
// every other case (not this type or flow, or no B-create sibling).
// Since 2026-10-26 the same for a B-recover message whose label is recovered
// at Process by its "recover and send" step (params.recoverAndSend, the link
// not known when the suggestion was built).
export function resendLabelCreateSiblingIndex(action, proposedActions) {
  if (action?.type !== 'resend_return_label_send_message') return null
  if (action?.params?.recoverAndSend === true && !action?.params?.labelUrl) {
    const idx = (proposedActions ?? []).findIndex((a) => a.type === 'resend_return_label' && a.params?.recoverAndSend === true)
    return idx >= 0 ? idx : null
  }
  if (action?.params?.flow !== 'B-create') return null
  const idx = (proposedActions ?? []).findIndex((a) => a.type === 'resend_return_label' && a.params?.flow === 'B-create')
  return idx >= 0 ? idx : null
}

// automated_return_notice_send_message depends on a SIBLING action's result
// ONLY when the label step is CREATING a brand-new label (no known
// trackingNumber at proposal time — actions.js's automatedReturnNoticeDefaultActions()
// leaves params.trackingNumber null in that case) — the label doesn't exist
// until that sibling succeeds, same "gate Process, pull the result forward"
// shape as resendLabelCreateSiblingIndex above.
// When the label already existed (params.trackingNumber set — the "resend"
// case), the real labelUrl was already known at proposal time and baked into
// this step's own params.body, so there's no sibling dependency at all —
// returns null, same as when there's no matching sibling in the array.
export function automatedReturnNoticeLabelSiblingIndex(action, proposedActions) {
  if (action?.type !== 'automated_return_notice_send_message') return null
  const idx = (proposedActions ?? []).findIndex((a) => a.type === 'automated_return_notice_resend_label' && !a.params?.trackingNumber)
  return idx >= 0 ? idx : null
}

// issue_ebay_return_refund_add_note (actions.js's automatedReturnNoticeRefundOnlyActions())
// depends entirely on its sibling issue_ebay_return_refund's own result — the
// refund type/amount, and therefore the note text describing it, aren't
// known until that sibling actually succeeds (there is no "already known at
// proposal time" case the way automated_return_notice_send_message's resend
// path has — every refund here is genuinely proposed, not yet decided).
// Returns the sibling's index so the UI can both gate this step's Process
// button until that index's stepStates status is 'success' (same shape as
// resendLabelCreateSiblingIndex above) and seed
// this step's own editedNote once it does (see SuggestionPanel.jsx's
// handleProcessStep()).
export function issueEbayReturnRefundNoteSiblingIndex(action, proposedActions) {
  if (action?.type !== 'issue_ebay_return_refund_add_note') return null
  const idx = (proposedActions ?? []).findIndex((a) => a.type === 'issue_ebay_return_refund')
  return idx >= 0 ? idx : null
}

// --- An eBay refund step's order note (2026-10-29) ---------------------------
// The note is in the refund step (server/src/ai/note-fold.js). A step with an
// empty note box (params.notes '': Other Actions' eBay refund, the
// automated-return-notice refund) starts with the same default note the
// refund modal prefills, from the refund type, amount and comment; it follows
// them until the person types their own (a note the person typed is never
// overwritten). A step the pipeline filled with the drafted note keeps it.
export const EBAY_REFUND_NOTE_TYPES = Object.freeze(['create_ebay_refund', 'issue_ebay_return_refund', 'inr_issue_refund'])
export function takesEbayRefundDefaultNote(a) {
  return EBAY_REFUND_NOTE_TYPES.includes(a?.type) && a?.params?.notes === ''
}
export function ebayRefundNoteDefault({ viaInquiry = false, ebayCase = '', returnId = null, onReturn = false, refundType = null, amount = '', orderTotal = 0, comment = '' } = {}) {
  if (viaInquiry) return `Refund issued through eBay INR inquiry ${ebayCase ?? ''} (no return required) — Full refund. ${comment ?? ''}`.trim()
  if (!refundType) return ''
  const value = refundType === 'full' ? orderTotal : amount
  const returnIdSuffix = onReturn && returnId ? ` against existing return ${returnId}` : ''
  return `Refund issued directly via eBay${returnIdSuffix} (no return required) — ${refundType === 'full' ? 'Full' : 'Partial'} refund of $${value}. ${comment ?? ''}`.trim()
}
// The default note for a step and its inputs ({ refundType, amount, comment,
// editedMessage }).
export function ebayRefundStepNoteDefault(a, input = {}) {
  const viaInquiry = a?.type === 'inr_issue_refund'
  return ebayRefundNoteDefault({
    viaInquiry,
    ebayCase: a?.params?.ebayCase ?? '',
    returnId: a?.params?.returnId ?? null,
    onReturn: a?.type === 'issue_ebay_return_refund',
    refundType: viaInquiry ? 'full' : (input?.refundType ?? null),
    amount: input?.amount ?? '',
    orderTotal: a?.params?.orderTotal ?? 0,
    comment: viaInquiry ? (input?.editedMessage ?? a?.params?.inquiryRefundBody?.comments?.content ?? '') : (input?.comment ?? ''),
  })
}
// The step's input after a change (patch): the note follows the default while
// it is empty or still the previous default; a typed note stays.
export function withEbayRefundNote(a, prev = {}, patch = {}) {
  const next = { ...(prev ?? {}), ...patch }
  if (!takesEbayRefundDefaultNote(a) || 'editedNote' in patch) return next
  const current = prev?.editedNote ?? ''
  if (current.trim() && current !== ebayRefundStepNoteDefault(a, prev)) return next
  return { ...next, editedNote: ebayRefundStepNoteDefault(a, next) }
}

// Seeds SuggestionPanel's replySendError state from a suggestion's own
// persisted lastSendError (server/src/repositories/ai-suggestions.js's
// recordSendFailure) whenever apply() runs — not just live inside
// onDecide()'s own response handler, which sets this same state directly
// right after calling apply() — so a failed send survives a page reload or
// a conversation switch back to this one, not just the one HTTP response
// that caused it. Only surfaced while the conversation is genuinely still
// stuck pending: once it's closed some other way, or a newer suggestion
// with no error exists, an old failed attempt must not show a stale banner.
export function resolveReplySendErrorSeed(suggestion, conversationStatus) {
  return conversationStatus === 'pending' ? (suggestion?.lastSendError ?? null) : null
}

// --- Blank-draft signature seeding (2026-09-28, feature/blank-draft-signatures) ---
// Any reply box that opens blank starts with the conversation's own live
// signature template instead of nothing (Jenn's rule, 2026-09-28) — plain
// text for eBay's textarea, real HTML for RichReplyEditor, matching how each
// editor already takes content. '' (never resolvedSignatureTemplate itself)
// when there's no live template for this scope, so the box stays genuinely
// blank rather than showing raw HTML tags or inventing a signature.
export function signatureSeedFor(isEbayChannel, resolvedSignatureTemplate, htmlToPlainTextFn) {
  if (!resolvedSignatureTemplate) return ''
  return isEbayChannel ? htmlToPlainTextFn(resolvedSignatureTemplate) : resolvedSignatureTemplate
}

// Whether a freshly-applied suggestion's draft box should seed the resolved
// signature instead of the suggestion's own (empty) draftReply/humanReply —
// only when there's a real draft box to seed at all (hasNoDraftBoxOfItsOwn's
// three cases have no box to seed) AND the suggestion's own text is
// genuinely empty. A non-empty draftReply/humanReply — including one that
// happens to equal the signature already (e.g. after a prior apply() already
// seeded it) — is left completely alone, so a CSR's own typed text, or a
// prior seed, is never clobbered by re-applying the same suggestion.
export function shouldSeedSignatureFor({ suggestion, fromEbay, isEbayClientEmail, hasNoDraftBoxOfItsOwnFn, rawText }) {
  if (!suggestion) return false
  if (hasNoDraftBoxOfItsOwnFn({ fromEbay, intent: suggestion.intent, isEbayClientEmail })) return false
  return !rawText?.trim()
}

// Plain-English messages for routes/ai.js's manual-actions route's own 400/409
// error codes — api.js's postJson() throws a bare "<path> -> <status>" as
// e.message (a debugging string, useless to show a CSR) and attaches the
// parsed response body separately as e.body. Falls back to that raw message
// for anything this doesn't recognize (a network failure, an unexpected 500,
// a code added here later without a mapping) rather than showing nothing.
const MANUAL_ACTION_ERROR_MESSAGES = {
  invalid_action_type: 'This action type is not supported.',
  company_not_supported_for_actions: 'Order actions are not set up for this account yet.',
  no_order_found: 'No matching order could be found for this conversation.',
  no_replacements_found: 'This order has no replacement documents to work with.',
  no_rmas_found: 'This order has no RMAs to work with.',
  not_ebay_channel: 'This action is only available for an eBay order.',
}
export function manualActionErrorMessage(e) {
  return MANUAL_ACTION_ERROR_MESSAGES[e?.body?.error] ?? e?.message ?? 'Something went wrong.'
}

// routes/ai.js's POST /conversations/:id/resend-confirmation error codes ->
// plain messages for ManualActionsSidebar.jsx's Resend Order/Shipping
// Confirmation Email modals, never a raw URL or stack trace. trq_error's own
// detail is the TRQ client's short error text (e.g. "TRQ 500"), not a stack
// trace, so it's safe to show. err is the Error api.js's postJson() throws,
// carrying the parsed response body on err.body.
export function resendConfirmationErrorMessage(err) {
  const code = err?.body?.error
  if (code === 'no_order_found') return 'No order could be found for this contact.'
  if (code === 'invalid_email') return 'Enter a single valid email address.'
  if (code === 'trq_write_disabled') return 'TRQ writes are currently disabled.'
  if (code === 'trq_error') return err.body?.detail ? `TRQ error: ${err.body.detail}` : 'TRQ returned an error.'
  return 'Something went wrong.'
}


// --- Replacement order number (docs/decisions.md, 2026-10-06) -------------

// The replacement's ERP order number from a getErpReplacementId() response,
// or null: the same shapes the executor's erpReplacementIdFrom() accepts.
export function erpReplacementIdFromResponse(response) {
  const raw = typeof response === 'string' ? response : (response?.erpOrderId ?? response?.id ?? null)
  if (raw == null) return null
  const value = String(raw).trim()
  return value || null
}

// Step states that survive a reload, rebuilt from the suggestion's own API
// call log (model_meta.apiCalls, each call tagged with the step that made
// it). Covers the replacement steps only: a process_replacement that created
// a replacement must show as done, never offer Process again (a second
// createReplacement would be a duplicate order), and must keep enabling its
// return-label sibling; a get_erp_replacement_id that found the number is
// done too.
export function restoredStepStates(suggestion) {
  const calls = suggestion?.modelMeta?.apiCalls ?? []
  const erpFor = (index) => {
    for (let k = calls.length - 1; k >= 0; k--) {
      const c = calls[k]
      if (c?.label === 'getErpReplacementId' && c.stepIndex === index && !c.error) {
        const id = erpReplacementIdFromResponse(c.response)
        if (id) return id
      }
    }
    return null
  }
  const states = {}
  ;(suggestion?.proposedActions ?? []).forEach((a, i) => {
    if (a?.type === 'process_replacement') {
      const created = calls.find((c) => c?.label === 'createReplacement' && c.stepIndex === i && !c.error)
      if (!created) return
      const erpReplacementId = erpFor(i)
      states[i] = { status: 'success', error: null, result: { id: created.response?.id ?? null, erpReplacementId, ...(erpReplacementId ? {} : { erpReplacementPending: true }) } }
    } else if (a?.type === 'get_erp_replacement_id') {
      const erpReplacementId = erpFor(i)
      if (erpReplacementId) states[i] = { status: 'success', error: null, result: { erpReplacementId } }
    }
  })
  // Return Status's overdue refund (2026-10-29, overdueRefund.js): the
  // refund's result, the email sent, the note added.
  return { ...states, ...overdueRestoredStates(suggestion) }
}

// What a step's Process control shows for its current state: 'done' (✓, no
// button: a succeeded step never runs again), 'retry' (a failed
// get_erp_replacement_id keeps its button, since it only reads and the number
// may simply not be there yet; a partly done return step too), 'failed'
// (✗), or 'ready'.
export function stepProcessGate(action, stepState) {
  const status = stepState?.status ?? 'idle'
  if (status === 'success') return 'done'
  // A return step left partly done (2026-10-29) keeps Process: it resumes
  // from the first piece not done, never making a second RMA or label. The
  // overdue refund's email to Support (2026-10-29) can be processed again
  // after a failed send; it never sends twice once sent.
  if (status === 'failed') return action?.type === 'get_erp_replacement_id' || action?.type === OVERDUE_EMAIL_STEP || stepState?.partlyDone === true ? 'retry' : 'failed'
  return 'ready'
}

// A task flow's eBay response steps (2026-10-14; server/src/ai/task-flows.js)
// that a processed step's resolved reply (result.replyText) fills: the eBay
// message steps of the same tracking path (taskPath; both unset when the flow
// has one path), and only after a step that resolves the reply
// (process_replacement, get_erp_replacement_id, task_create_refund).
export const TASK_REPLY_SOURCE_TYPES = Object.freeze(['process_replacement', 'get_erp_replacement_id', 'task_create_refund'])
export function taskEbayStepIndexes(proposedActions, processedIndex) {
  const actions = proposedActions ?? []
  const source = actions[processedIndex]
  if (!source || !TASK_REPLY_SOURCE_TYPES.includes(source.type)) return []
  const path = source.taskPath ?? null
  return actions
    .map((a, i) => (['send_ebay_member_message', 'inr_send_message', 'task_case_message'].includes(a?.type) && (a.taskPath ?? null) === path ? i : -1))
    .filter((i) => i >= 0)
}

// The moved / not-moved choice on a task (2026-10-15; server/src/ai/task-flows.js).
// A step with a taskPath shows only when that path is chosen.
export function isTaskPathHidden(action, choice) {
  return Boolean(action?.taskPath) && action.taskPath !== choice
}
// The email reply a path's response step carries (task_reply_template), or
// null (an eBay path answers in its own steps).
export function taskReplyTextFor(proposedActions, path) {
  const step = (proposedActions ?? []).find((a) => a?.type === 'task_reply_template' && (a.taskPath ?? null) === path)
  return step ? (step.params?.text ?? '') : null
}
// A Return Status stage's reply with its date filled (2026-10-21; @cxhub/
// shared's fillReturnStageDates()): the carrier's date in INSERTDATE, and on
// delivered the expected-by date. Without a date, the template as is.
// A delivered return past its refund window (2026-10-29, @cxhub/shared's
// return-refund-overdue.js): the overdue path's reply (Refund Confirmation,
// filled when built). The Refund Delivered reply's "by" date uses the same
// window. today: for tests.
export function returnStageText(proposedActions, path, date, today = undefined) {
  const step = taskPathChoiceStep(proposedActions)
  if (returnStagePathKey(step, { choice: path, date }, today) === RETURN_STAGE_DELIVERED_OVERDUE) {
    const overdue = taskReplyTextFor(proposedActions, RETURN_STAGE_DELIVERED_OVERDUE)
    if (overdue != null) return overdue
  }
  const text = taskReplyTextFor(proposedActions, path)
  const window = refundOverdueWindowOf(step)
  return text == null ? null : fillReturnStageDates(text, { stage: path, date, ...(window != null ? { expectedBusinessDays: window } : {}) })
}
// The stage's reply step's template title, for the prompt (null when none).
// date (2026-10-29): a delivered stage past its refund window names the
// overdue reply's title.
export function taskReplyTitleFor(proposedActions, path, date = null, today = undefined) {
  const key = date ? returnStagePathKey(taskPathChoiceStep(proposedActions), { choice: path, date }, today) : path
  const find = (p) => (proposedActions ?? []).find((a) => a?.type === 'task_reply_template' && (a.taskPath ?? null) === p)
  const step = find(key) ?? find(path)
  return step?.params?.templateTitle ?? null
}
// The sales-hold path choice's Fix the address reply (2026-10-27): an empty
// reply step, there only so choosing the path empties the reply box; never
// shown as a step.
export function isEmptyPathReply(action) {
  return action?.type === 'task_reply_template' && !String(action?.params?.text ?? '').trim()
}

// An ambiguous address's candidates (2026-10-27, the Fix the address path):
// each Smarty candidate and the address on file, as the picker lists them.
export function addressCandidateLabel(candidate) {
  const where = candidate?.source === 'on_file' ? 'Address on file' : `Smarty candidate ${(candidate?.index ?? 0) + 1}`
  return `${where}: ${addressOneLine(candidate?.address)}`
}

// The inputs after a person picks a candidate on step `index`: that address
// in the update step (every field a string, as the box edits them), the
// pick's index, and the hold removal's note on the same path naming the pick
// and that a person chose it (@cxhub/shared's chosenCandidateNote()).
export function applyAddressCandidatePick(proposedActions, inputs, index, candidateIndex) {
  const actions = proposedActions ?? []
  const step = actions[index]
  const candidate = (step?.params?.candidates ?? [])[candidateIndex]
  if (!candidate) return inputs
  const a = candidate.address ?? {}
  const editedAddress = Object.fromEntries(['shippingAddress1', 'shippingAddress2', 'shippingCity', 'shippingRegion', 'shippingPostalCode'].map((f) => [f, a[f] ?? '']))
  const next = { ...(inputs ?? {}), [index]: { ...((inputs ?? {})[index] ?? {}), editedAddress, candidateIndex } }
  const holdIndex = actions.findIndex((x) => x?.type === 'sales_hold_address_validation_remove_hold' && x?.params?.chosenAddressNote && (x.taskPath ?? null) === (step.taskPath ?? null))
  if (holdIndex >= 0) {
    next[holdIndex] = { ...(next[holdIndex] ?? {}), editedNote: chosenCandidateNote({ address: a, source: candidate.source, index: candidate.index ?? candidateIndex, count: candidate.count ?? 0 }) }
  }
  return next
}

// Whether the agent edited a note or message box on a path (compared with the
// applied baseline): switching away from it would discard those edits.
export function taskPathHasEdits(proposedActions, inputs, baseline, path) {
  return (proposedActions ?? []).some((a, i) => {
    if (!path || a?.taskPath !== path) return false
    const live = inputs?.[i] ?? {}
    const base = baseline?.[i] ?? {}
    return (live.editedNote != null && live.editedNote !== base.editedNote)
      || (live.editedMessage != null && live.editedMessage !== base.editedMessage)
  })
}
// The inputs with a hidden path's steps back to their defaults, so its edits
// are dropped; every other step keeps the agent's input.
export function resetTaskPathInputs(proposedActions, inputs, path, resolvePlaceholder = (text) => text) {
  const fresh = reseedActionInputs(proposedActions, {}, resolvePlaceholder)
  const next = { ...(inputs ?? {}) }
  ;(proposedActions ?? []).forEach((a, i) => {
    if (a?.taskPath !== path) return
    if (fresh[i] === undefined) delete next[i]
    else next[i] = fresh[i]
  })
  return next
}

// (Moved from SuggestionPanel.jsx, which re-exports both, 2026-10-23.)
// Group an item-selection table's rows by lineSequence so kit/standalone rows
// appear above their components (mirrors enrichment.js's orderFactsText()
// grouping) — components render indented beneath their parent kit.
export function groupItemsForTable(allItems) {
  const grouped = []
  const seqMap = new Map()
  for (const it of allItems ?? []) {
    const seq = it.lineSequence
    if (seq != null) {
      if (!seqMap.has(seq)) { seqMap.set(seq, []); grouped.push(seqMap.get(seq)) }
      seqMap.get(seq).push(it)
    } else {
      grouped.push([it])
    }
  }
  return grouped.flatMap((members) =>
    [...members].sort((a) => a.itemType === 'kit' ? -1 : a.itemType === 'component' ? 1 : 0)
  )
}

// A component row is blocked (disabled) when ITS OWN kit sibling (same
// lineSequence) is currently selected — selecting the kit already covers
// every one of its components, so selecting both would double up the same
// physical parts on the return/replacement. `selection` is the current
// `items` array ({ sku, quantity } entries), same shape both call sites use.
export function isComponentBlockedByKit(it, allItems, selection) {
  if (it.itemType !== 'component' || it.lineSequence == null) return false
  const kitSibling = (allItems ?? []).find((x) => x.lineSequence === it.lineSequence && x.itemType === 'kit')
  return Boolean(kitSibling && (selection ?? []).some((s) => s.sku === kitSibling.sku))
}
